
import { HttpClient, HttpEvent } from '@angular/common/http';
import { Inject, Injectable, Renderer2, RendererFactory2, DOCUMENT } from '@angular/core';
import { Observable } from 'rxjs';

import { CustomClass } from './config';

export interface UploadResponse {
    imageUrl: string;
}

@Injectable()
export class AngularEditorService {
    savedSelection: Range | null;
    selectedText: string;
    uploadUrl: string;
    uploadWithCredentials: boolean;
    private renderer: Renderer2;

    constructor(private http: HttpClient, @Inject(DOCUMENT) private doc: any, rendererFactory: RendererFactory2) {
        this.renderer = rendererFactory.createRenderer(null, null);
    }

    /**
     * Executed command from editor header buttons exclude toggleEditorMode
     * @param command string from triggerCommand
     * @param value
     */
    executeCommand(command: string, value?: string) {
        const commands = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'p', 'pre'];
        if (commands.includes(command)) {
            this.doc.execCommand('formatBlock', false, command);
            return;
        }

        // Handle indentation commands with CSP-compliant approach
        if (command === 'indent' || command === 'outdent') {
            this.applyIndentation(command);
            return;
        }

        // Handle style-related commands with CSS classes instead of inline styles
        if (this.isStyleCommand(command)) {
            this.applyStyleWithClass(command, value);
            return;
        }

        this.doc.execCommand(command, false, value);
    }

    /**
     * Check if command is style-related that should use CSS classes
     */
    private isStyleCommand(command: string): boolean {
        const styleCommands = [
            'foreColor',
            'hiliteColor',
            'fontName',
            'fontSize',
            'justifyLeft',
            'justifyCenter',
            'justifyRight',
            'justifyFull',
            'insertUnorderedList',
            'insertOrderedList',
            'bold',
            'italic',
            'underline',
            'strikeThrough',
            'subscript',
            'superscript',
        ];
        return styleCommands.includes(command);
    }

    /**
     * Apply indentation using CSS classes only (CSP-compliant)
     */
    private applyIndentation(command: string): void {
        const selection = this.doc.getSelection();
        if (!selection || selection.rangeCount === 0) return;

        const range = selection.getRangeAt(0);

        // Find the block element to indent
        let blockElement = this.findBlockElementForIndent(range);
        if (!blockElement) {
            // If no block element found, wrap the content in a div
            blockElement = this.wrapInBlockElement(range);
        }

        // Get current indent level
        const currentLevel = this.getIndentLevel(blockElement);

        // Calculate new indent level
        let newLevel = currentLevel;
        if (command === 'indent') {
            newLevel = Math.min(currentLevel + 1, 4); // Max 4 levels
        } else {
            newLevel = Math.max(currentLevel - 1, 0); // Min 0 levels
        }

        // Remove old indent class
        if (currentLevel > 0) {
            this.renderer.removeClass(blockElement, `ae-indent-level-${currentLevel}`);
        }

        // Add new indent class
        if (newLevel > 0) {
            this.renderer.addClass(blockElement, `ae-indent-level-${newLevel}`);
        }
    }

    /**
     * Find the block element for indentation
     */
    private findBlockElementForIndent(range: Range): Element | null {
        let container = range.commonAncestorContainer;

        // If we're in a text node, get its parent
        if (container.nodeType === Node.TEXT_NODE) {
            container = container.parentElement;
        }

        // Find the nearest block element, but exclude the editor textarea itself
        while (container && container !== this.doc.body) {
            if (container instanceof Element) {
                // Skip the editor textarea container
                if (container.classList.contains('angular-editor-textarea')) {
                    return null;
                }

                const tagName = container.tagName.toLowerCase();
                if (['p', 'div', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'li', 'blockquote'].includes(tagName)) {
                    return container;
                }
            }
            container = container.parentElement;
        }

        return null;
    }

    /**
     * Wrap content in a block element for indentation
     */
    private wrapInBlockElement(range: Range): Element {
        const div = this.renderer.createElement('div');

        if (range.collapsed) {
            // If collapsed, just insert the div at cursor position
            div.innerHTML = '<br>'; // Placeholder
            range.insertNode(div);

            // Move cursor inside the div
            const newRange = this.doc.createRange();
            newRange.selectNodeContents(div);
            newRange.collapse(true);
            const sel = this.doc.getSelection();
            sel.removeAllRanges();
            sel.addRange(newRange);
        } else {
            // Wrap selected content
            const contents = range.extractContents();
            this.renderer.appendChild(div, contents);
            range.insertNode(div);
        }

        return div;
    }

    /**
     * Get current indent level from element classes
     */
    private getIndentLevel(element: Element): number {
        for (let i = 4; i >= 1; i--) {
            if (element.classList.contains(`ae-indent-level-${i}`)) {
                return i;
            }
        }
        return 0;
    }

    /**
     * Apply styles using CSS classes instead of inline styles for CSP compliance
     */
    private applyStyleWithClass(command: string, value?: string): void {
        const selection = this.doc.getSelection();
        if (!selection || selection.rangeCount === 0) return;

        const range = selection.getRangeAt(0);
        const selectedText = range.toString();

        // Handle list commands differently - they need list elements (toggle functionality)
        if (command === 'insertUnorderedList' || command === 'insertOrderedList') {
            // Check if we're already in a list and what type
            const currentListType = this.getCurrentListType(range);
            const requestedListType = command === 'insertUnorderedList' ? 'ul' : 'ol';

            if (currentListType) {
                if (currentListType === requestedListType) {
                    // Same list type - remove list formatting
                    this.removeListStyle(range);
                } else {
                    // Different list type - convert the list
                    this.convertListType(range, requestedListType);
                }
            } else {
                // Not in a list - apply list formatting (works with or without selected text)
                this.applyListStyle(command, range, selectedText);
            }
            return;
        }

        // Handle alignment commands - apply to block elements (works with collapsed selections)
        if (command.startsWith('justify')) {
            this.applyAlignmentStyle(command, range);
            return;
        }

        // Handle color commands - work with collapsed selections by applying to current position
        if (command === 'foreColor' || command === 'hiliteColor') {
            this.applyColorStyle(command, value, range, selectedText);
            return;
        }

        // Handle toggle commands (bold, italic, underline, strikeThrough, subscript, superscript)
        if (['bold', 'italic', 'underline', 'strikeThrough', 'subscript', 'superscript'].includes(command)) {
            this.applyToggleStyle(command, range, selectedText);
            return;
        }

        // Handle text styling (fonts, sizes, position) - needs text selection
        if (!selectedText) return;

        this.applyTextStyle(command, value, range, selectedText);

        // Clear selection
        selection.removeAllRanges();
    }

    /**
     * Apply toggle styling (bold, italic, underline, strikeThrough)
     */
    private applyToggleStyle(command: string, range: Range, selectedText: string): void {
        const className = this.getCssClassForCommand(command);

        // Check if any element in the selection already has this style
        const hasStyle = this.checkSelectionHasStyle(className, range);

        if (hasStyle) {
            // Remove the style from selected elements
            this.removeStyleFromSelection(className, range);
        } else {
            // Apply the style
            if (selectedText) {
                // Walk up the DOM tree to find an existing span we can reuse
                let node: Node | null = range.commonAncestorContainer;
                if (node.nodeType === Node.TEXT_NODE) {
                    node = node.parentElement;
                }

                let targetSpan: Element | null = null;

                // Walk up to find a span that contains exactly the selected text
                while (node && node !== this.doc.body) {
                    if (node instanceof Element) {
                        if (node.tagName.toLowerCase() === 'span') {
                            const spanText = node.textContent;
                            if (spanText === selectedText) {
                                targetSpan = node;
                                break;
                            }
                        }
                        // Stop at the editor container
                        if (node.classList.contains('angular-editor-textarea')) {
                            break;
                        }
                    }
                    node = node.parentElement;
                }

                if (targetSpan) {
                    // Add class to existing span
                    this.renderer.addClass(targetSpan, className);

                    // Restore selection
                    const newRange = this.doc.createRange();
                    newRange.selectNodeContents(targetSpan);
                    const sel = this.doc.getSelection();
                    sel.removeAllRanges();
                    sel.addRange(newRange);
                } else {
                    // Create new span with style class
                    const span = this.renderer.createElement('span');
                    // Extract contents instead of creating text node to preserve inner HTML
                    const contents = range.extractContents();
                    this.renderer.appendChild(span, contents);
                    this.renderer.addClass(span, className);

                    // Insert the styled span
                    range.insertNode(span);

                    // Restore selection on the newly created span
                    const newRange = this.doc.createRange();
                    newRange.selectNodeContents(span);
                    const sel = this.doc.getSelection();
                    sel.removeAllRanges();
                    sel.addRange(newRange);
                }
            } else {
                // Apply style class to cursor position
                const targetElement = this.findOrCreateTargetForColor(range);
                if (targetElement) {
                    this.renderer.addClass(targetElement, className);
                }
            }
        }
    }

    /**
     * Check if a style command is currently active
     */
    isCommandActive(command: string): boolean {
        const selection = this.doc.getSelection();
        if (!selection || selection.rangeCount === 0) return false;

        const range = selection.getRangeAt(0);

        // Special handling for list commands - check for ul/ol elements
        if (command === 'insertUnorderedList') {
            const currentListType = this.getCurrentListType(range);
            return currentListType === 'ul';
        }
        if (command === 'insertOrderedList') {
            const currentListType = this.getCurrentListType(range);
            return currentListType === 'ol';
        }

        const className = this.getCssClassForCommand(command);

        // Check the current node and all its parents for the style class
        let node: Node | null = range.commonAncestorContainer;

        // If it's a text node, start from its parent
        if (node.nodeType === Node.TEXT_NODE) {
            node = node.parentElement;
        }

        // Walk up the DOM tree checking for the class
        while (node && node !== this.doc.body) {
            if (node instanceof Element) {
                if (node.classList.contains(className)) {
                    return true;
                }
                // Stop at the editor container
                if (node.classList.contains('angular-editor-textarea')) {
                    break;
                }
            }
            node = node.parentElement;
        }

        return false;
    }

    /**
     * Check if selection already has the specified style
     */
    private checkSelectionHasStyle(className: string, range: Range): boolean {
        // Walk up the DOM tree to check if any parent has this class
        let node: Node | null = range.commonAncestorContainer;

        // If it's a text node, start from its parent
        if (node.nodeType === Node.TEXT_NODE) {
            node = node.parentElement;
        }

        // Walk up the DOM tree checking for the class
        while (node && node !== this.doc.body) {
            if (node instanceof Element) {
                if (node.classList.contains(className)) {
                    return true;
                }
                // Stop at the editor container
                if (node.classList.contains('angular-editor-textarea')) {
                    break;
                }
            }
            node = node.parentElement;
        }

        return false;
    }

    /**
     * Remove style class from selected elements
     */
    private removeStyleFromSelection(className: string, range: Range): void {
        // Walk up the DOM tree to find ALL elements with this class
        let node: Node | null = range.commonAncestorContainer;

        // If it's a text node, start from its parent
        if (node.nodeType === Node.TEXT_NODE) {
            node = node.parentElement;
        }

        // Collect all elements with the class we want to remove
        const elementsToClean: Element[] = [];
        let lastElement: Element | null = null;

        while (node && node !== this.doc.body) {
            if (node instanceof Element) {
                if (node.classList.contains(className)) {
                    elementsToClean.push(node);
                    lastElement = node;
                }
                // Stop at the editor container
                if (node.classList.contains('angular-editor-textarea')) {
                    break;
                }
            }
            node = node.parentElement;
        }

        // Remove the class from all found elements
        let parentForSelection: Node | null = null;
        for (const element of elementsToClean) {
            const parent = element.parentNode;
            this.renderer.removeClass(element, className);

            // If the span has no other classes or attributes, unwrap it
            if (element.tagName.toLowerCase() === 'span' && element.classList.length === 0 && element.attributes.length === 0) {
                this.unwrapElement(element);
                if (!parentForSelection) {
                    parentForSelection = parent;
                }
            }
        }

        // Restore selection after removal
        const sel = this.doc.getSelection();
        if (sel) {
            const newRange = this.doc.createRange();
            try {
                if (lastElement && lastElement.parentNode) {
                    // Element still exists, select its contents
                    newRange.selectNodeContents(lastElement);
                } else if (parentForSelection) {
                    // Element was unwrapped, select parent content
                    newRange.selectNodeContents(parentForSelection);
                }
                sel.removeAllRanges();
                sel.addRange(newRange);
            } catch {
                // Selection restoration failed, continue
            }
        }
    }

    /**
     * Unwrap an element, preserving its content
     */
    private unwrapElement(element: Element): void {
        const parent = element.parentNode;
        while (element.firstChild) {
            parent.insertBefore(element.firstChild, element);
        }
        parent.removeChild(element);
    }

    /**
     * Apply color styling to text
     */
    private applyColorStyle(command: string, value: string | undefined, range: Range, selectedText: string): void {
        // Generate a unique class name for this color
        const colorValue = value ? value.replace('#', '') : 'default';
        const className = command === 'foreColor' ? `ae-color-${colorValue}` : `ae-bgcolor-${colorValue}`;

        // Ensure the CSS class exists
        this.ensureColorClass(command, colorValue, className);

        if (selectedText) {
            // Wrap selected text in span with color class
            const span = this.renderer.createElement('span');
            // Extract contents to preserve inner HTML formatting
            const contents = range.extractContents();
            this.renderer.appendChild(span, contents);
            this.renderer.addClass(span, className);

            // Insert the styled span
            range.insertNode(span);
        } else {
            // Apply color class to cursor position
            const targetElement = this.findOrCreateTargetForColor(range);
            if (targetElement) {
                this.renderer.addClass(targetElement, className);
            }
        }

        // Clear selection
        this.doc.getSelection().removeAllRanges();
    }

    /**
     * Ensure CSS class exists for the color
     */
    private ensureColorClass(command: string, colorValue: string, className: string): void {
        // Check if class already exists
        const existingStyle = this.doc.querySelector(`style[data-color="${className}"]`);
        if (existingStyle) {
            return;
        }

        // Create CSS rule
        const property = command === 'foreColor' ? 'color' : 'background-color';
        const cssRule = `.${className} { ${property}: #${colorValue} !important; }`;

        // Create style element with nonce
        const styleEl = this.doc.createElement('style');
        styleEl.setAttribute('data-color', className);
        styleEl.textContent = cssRule;

        // Add CSP nonce
        const nonceMeta = this.doc.querySelector('meta[name="csp-nonce"]');
        if (nonceMeta) {
            const nonce = nonceMeta.getAttribute('content');
            if (nonce) {
                styleEl.setAttribute('nonce', nonce);
            }
        }

        this.doc.head.appendChild(styleEl);
    }

    /**
     * Find or create a target element for color application at cursor position
     */
    private findOrCreateTargetForColor(range: Range): Element | null {
        if (!range.collapsed) return null;

        // Try to find the nearest text-containing element
        let container = range.startContainer;

        if (container.nodeType === Node.TEXT_NODE) {
            container = container.parentElement;
        }

        // Look for a suitable parent element
        while (container && container !== this.doc.body) {
            if (container instanceof Element) {
                const tagName = container.tagName.toLowerCase();
                if (['p', 'div', 'span', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'li', 'td', 'th'].includes(tagName)) {
                    return container;
                }
            }
            container = container.parentElement;
        }

        // If no suitable element found, create a span
        const span = this.renderer.createElement('span');
        span.textContent = '\u200B'; // Zero-width space to keep it visible
        range.insertNode(span);

        // Move cursor inside the span
        const newRange = this.doc.createRange();
        newRange.selectNodeContents(span);
        newRange.collapse(true);
        const selection = this.doc.getSelection();
        selection.removeAllRanges();
        selection.addRange(newRange);

        return span;
    }
    /**
     * Get the current list type if selection is inside a list
     * Returns 'ul', 'ol', or null
     */
    private getCurrentListType(range: Range): 'ul' | 'ol' | null {
        // Use startContainer for better detection
        let node: Node | null = range.startContainer;

        if (node.nodeType === Node.TEXT_NODE) {
            node = node.parentElement;
        }

        // Walk up the DOM tree to find a list element
        while (node && node !== this.doc.body) {
            if (node instanceof Element) {
                const tagName = node.tagName.toLowerCase();
                if (tagName === 'ul' || tagName === 'ol') {
                    return tagName as 'ul' | 'ol';
                }
                if (node.classList && node.classList.contains('angular-editor-textarea')) {
                    break;
                }
            }
            node = node.parentElement;
        }

        return null;
    }

    /**
     * Convert a list from one type to another (ul <-> ol)
     */
    private convertListType(range: Range, newListType: 'ul' | 'ol'): void {
        let node: Node | null = range.startContainer;

        if (node.nodeType === Node.TEXT_NODE) {
            node = node.parentElement;
        }

        // Find the list element
        let listElement: Element | null = null;
        while (node && node !== this.doc.body) {
            if (node instanceof Element) {
                const tagName = node.tagName.toLowerCase();
                if (tagName === 'ul' || tagName === 'ol') {
                    listElement = node;
                    break;
                }
                if (node.classList && node.classList.contains('angular-editor-textarea')) {
                    break;
                }
            }
            node = node.parentElement;
        }

        if (listElement) {
            // Create new list element of the requested type
            const newList = this.renderer.createElement(newListType);

            // Copy all classes from old list to new list
            const classes = Array.from(listElement.classList);
            classes.forEach(className => {
                this.renderer.addClass(newList, className);
            });

            // Move all list items to the new list
            const listItems = Array.from(listElement.querySelectorAll(':scope > li'));
            listItems.forEach(li => {
                this.renderer.appendChild(newList, li);
            });

            // Replace old list with new list
            if (listElement.parentNode) {
                listElement.parentNode.replaceChild(newList, listElement);

                // Restore selection
                const sel = this.doc.getSelection();
                if (sel) {
                    const newRange = this.doc.createRange();
                    newRange.selectNodeContents(newList);
                    newRange.collapse(true);
                    sel.removeAllRanges();
                    sel.addRange(newRange);
                }
            }
        }
    }

    /**
     * Remove list formatting from the selection
     */
    private removeListStyle(range: Range): void {
        let node: Node | null = range.commonAncestorContainer;

        if (node.nodeType === Node.TEXT_NODE) {
            node = node.parentElement;
        }

        // Find the list element
        let listElement: Element | null = null;
        while (node && node !== this.doc.body) {
            if (node instanceof Element) {
                const tagName = node.tagName.toLowerCase();
                if (tagName === 'ul' || tagName === 'ol') {
                    listElement = node;
                    break;
                }
                if (node.classList.contains('angular-editor-textarea')) {
                    break;
                }
            }
            node = node.parentElement;
        }

        if (listElement) {
            // Extract text from all list items
            const listItems = Array.from(listElement.querySelectorAll('li'));
            const textContent = listItems.map(li => li.textContent).join('\n');

            // Create a text node with the content
            const textNode = this.doc.createTextNode(textContent);

            // Replace the list with the text
            if (listElement.parentNode) {
                listElement.parentNode.replaceChild(textNode, listElement);

                // Restore selection on the text
                const sel = this.doc.getSelection();
                if (sel) {
                    const newRange = this.doc.createRange();
                    newRange.selectNodeContents(textNode);
                    sel.removeAllRanges();
                    sel.addRange(newRange);
                }
            }
        }
    }

    private applyListStyle(command: string, range: Range, selectedText: string): void {
        const listType = command === 'insertUnorderedList' ? 'ul' : 'ol';
        const className = this.getCssClassForCommand(command);

        // Create list structure
        const listElement = this.renderer.createElement(listType);
        this.renderer.addClass(listElement, className);

        if (selectedText && selectedText.trim()) {
            // Split selected text by lines and create list items
            const lines = selectedText.split('\n').filter(line => line.trim());
            lines.forEach(line => {
                const listItem = this.renderer.createElement('li');
                const textNode = this.doc.createTextNode(line);
                this.renderer.appendChild(listItem, textNode);
                this.renderer.appendChild(listElement, listItem);
            });
        } else {
            // No text selected - create a single empty list item
            const listItem = this.renderer.createElement('li');
            const br = this.renderer.createElement('br');
            this.renderer.appendChild(listItem, br);
            this.renderer.appendChild(listElement, listItem);
        }

        // Replace the selected content with the list
        range.deleteContents();
        range.insertNode(listElement);

        // If no text was selected, place cursor inside the empty list item
        if (!selectedText || !selectedText.trim()) {
            const firstListItem = listElement.querySelector('li');
            if (firstListItem) {
                const newRange = this.doc.createRange();
                newRange.selectNodeContents(firstListItem);
                newRange.collapse(true);
                const sel = this.doc.getSelection();
                sel.removeAllRanges();
                sel.addRange(newRange);
            }
        }
    }

    /**
     * Apply alignment styling to block elements
     */
    private applyAlignmentStyle(command: string, range: Range): void {
        const className = this.getCssClassForCommand(command);

        // Find the nearest block element or create one
        const blockElement = this.findOrCreateBlockElement(range);

        // Remove any existing alignment classes
        this.removeAlignmentClasses(blockElement);

        // Add the new alignment class
        this.renderer.addClass(blockElement, className);
    }

    /**
     * Remove all alignment classes from an element
     */
    private removeAlignmentClasses(element: Element): void {
        const alignmentClasses = ['ae-text-left', 'ae-text-center', 'ae-text-right', 'ae-text-justify'];
        alignmentClasses.forEach(className => {
            if (element.classList.contains(className)) {
                this.renderer.removeClass(element, className);
            }
        });
    }

    /**
     * Apply text styling (colors, fonts, sizes, position)
     */
    private applyTextStyle(command: string, value: string | undefined, range: Range, _selectedText: string): void {
        const className = this.getCssClassForCommand(command, value);

        // Create a span with appropriate CSS class
        const span = this.renderer.createElement('span');
        // Extract contents to preserve inner HTML formatting
        const contents = range.extractContents();
        this.renderer.appendChild(span, contents);
        this.renderer.addClass(span, className);

        // Insert the styled span
        range.insertNode(span);
    }

    /**
     * Find or create a block element for alignment
     */
    private findOrCreateBlockElement(range: Range): Element {
        let container = range.commonAncestorContainer;

        // If we're in a text node, get its parent
        if (container.nodeType === Node.TEXT_NODE) {
            container = container.parentElement;
        }

        // Find the nearest block element
        while (container && container !== this.doc.body) {
            if (container instanceof Element) {
                const tagName = container.tagName.toLowerCase();
                if (['p', 'div', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'li', 'td', 'th'].includes(tagName)) {
                    return container;
                }
            }
            container = container.parentElement;
        }

        // If no block element found and we have a selection, wrap it in a div
        if (range.collapsed) {
            // If selection is collapsed, find or create a suitable container
            const newDiv = this.renderer.createElement('div');
            newDiv.innerHTML = '<br>'; // Add placeholder content
            range.insertNode(newDiv);
            return newDiv;
        } else {
            // Wrap the selected content in a div
            const div = this.renderer.createElement('div');
            const contents = range.extractContents();
            this.renderer.appendChild(div, contents);
            range.insertNode(div);
            return div;
        }
    }

    /**
     * Get CSS class name for editor commands
     */
    private getCssClassForCommand(command: string, value?: string): string {
        switch (command) {
            case 'foreColor':
                return `ae-text-color-${value?.replace('#', '') || 'default'}`;
            case 'hiliteColor':
                return `ae-bg-color-${value?.replace('#', '') || 'default'}`;
            case 'fontName':
                return `ae-font-${value?.replace(/\s+/g, '-').toLowerCase() || 'default'}`;
            case 'fontSize':
                return `ae-font-size-${value || 'default'}`;
            case 'justifyLeft':
                return 'ae-text-left';
            case 'justifyCenter':
                return 'ae-text-center';
            case 'justifyRight':
                return 'ae-text-right';
            case 'justifyFull':
                return 'ae-text-justify';
            case 'subscript':
                return 'ae-subscript';
            case 'superscript':
                return 'ae-superscript';
            case 'insertUnorderedList':
                return 'ae-list-unordered';
            case 'insertOrderedList':
                return 'ae-list-ordered';
            case 'bold':
                return 'ae-bold';
            case 'italic':
                return 'ae-italic';
            case 'underline':
                return 'ae-underline';
            case 'strikeThrough':
                return 'ae-strikethrough';
            default:
                return 'ae-styled-text';
        }
    }

    /**
     * Create URL link
     * @param url string from UI prompt
     */
    createLink(url: string) {
        if (!url.includes('http')) {
            this.doc.execCommand('createlink', false, url);
        } else {
            const newUrl = '<a href="' + url + '" target="_blank">' + this.selectedText + '</a>';
            this.insertHtml(newUrl);
        }
    }

    /**
     * insert color either font or background
     *
     * @param color color to be inserted
     * @param where where the color has to be inserted either text/background
     */
    insertColor(color: string, where: string): void {
        const restored = this.restoreSelection();
        if (restored) {
            const command = where === 'textColor' ? 'foreColor' : 'hiliteColor';
            this.applyStyleWithClass(command, color);
        }
    }

    /**
     * Set font name
     * @param fontName string
     */
    setFontName(fontName: string) {
        this.applyStyleWithClass('fontName', fontName);
    }

    /**
     * Set font size
     * @param fontSize string
     */
    setFontSize(fontSize: string) {
        this.applyStyleWithClass('fontSize', fontSize);
    }

    /**
     * Create raw HTML
     * @param html HTML string
     */
    insertHtml(html: string): void {
        const isHTMLInserted = this.doc.execCommand('insertHTML', false, html);

        if (!isHTMLInserted) {
            throw new Error('Unable to perform the operation');
        }
    }

    /**
     * save selection when the editor is focussed out
     */
    public saveSelection = (): void => {
        if (this.doc.getSelection) {
            const sel = this.doc.getSelection();
            if (sel.getRangeAt && sel.rangeCount) {
                this.savedSelection = sel.getRangeAt(0);
                this.selectedText = sel.toString();
            } else if (this.doc.createRange) {
                this.savedSelection = document.createRange();
            } else {
                this.savedSelection = null;
            }
        }
    };

    /**
     * restore selection when the editor is focused in
     *
     * saved selection when the editor is focused out
     */
    restoreSelection(): boolean {
        if (this.savedSelection) {
            if (this.doc.getSelection) {
                const sel = this.doc.getSelection();
                sel.removeAllRanges();
                sel.addRange(this.savedSelection);
                return true;
            }
            return false;
        } else {
            return false;
        }
    }

    /**
     * setTimeout used for execute 'saveSelection' method in next event loop iteration
     */
    public executeInNextQueueIteration(callbackFn: (...args: any[]) => any, timeout = 1e2): void {
        setTimeout(callbackFn, timeout);
    }

    /** check any selection is made or not */
    // private checkSelection(): any {

    //   const selectedText = this.savedSelection.toString();

    //   if (selectedText.length === 0) {
    //     throw new Error('No Selection Made');
    //   }
    //   return true;
    // }

    /**
     * Upload file to uploadUrl
     * @param file The file
     */
    uploadImage(file: File): Observable<HttpEvent<UploadResponse>> {
        const uploadData: FormData = new FormData();

        uploadData.append('file', file, file.name);

        return this.http.post<UploadResponse>(this.uploadUrl, uploadData, {
            reportProgress: true,
            observe: 'events',
            withCredentials: this.uploadWithCredentials,
        });
    }

    /**
     * Insert image with Url
     * @param imageUrl The imageUrl.
     */
    insertImage(imageUrl: string) {
        this.doc.execCommand('insertImage', false, imageUrl);
    }

    setDefaultParagraphSeparator(separator: string) {
        this.doc.execCommand('defaultParagraphSeparator', false, separator);
    }

    createCustomClass(customClass: CustomClass) {
        let newTag = this.selectedText;
        if (customClass) {
            const tagName = customClass.tag ? customClass.tag : 'span';
            newTag = '<' + tagName + ' class="' + customClass.class + '">' + this.selectedText + '</' + tagName + '>';
        }
        this.insertHtml(newTag);
    }

    insertVideo(videoUrl: string) {
        if (videoUrl.match('www.youtube.com')) {
            this.insertYouTubeVideoTag(videoUrl);
        }
        if (videoUrl.match('vimeo.com')) {
            this.insertVimeoVideoTag(videoUrl);
        }
    }

    private insertYouTubeVideoTag(videoUrl: string): void {
        const id = videoUrl.split('v=')[1];
        const imageUrl = `https://img.youtube.com/vi/${id}/0.jpg`;
        const thumbnail = `
  <div class="video-thumbnail">
    <a href="${videoUrl}" target="_blank">
      <img class="video-thumbnail-image" src="${imageUrl}" alt="click to watch"/>
      <img class="video-play-icon" src="https://img.icons8.com/color/96/000000/youtube-play.png"/>
    </a>
  </div>`;
        this.insertHtml(thumbnail);
    }

    private insertVimeoVideoTag(videoUrl: string): void {
        const sub = this.http.get<any>(`https://vimeo.com/api/oembed.json?url=${videoUrl}`).subscribe(data => {
            const imageUrl = data.thumbnail_url_with_play_button;
            const thumbnail = `<div>
        <a href='${videoUrl}' target='_blank'>
          <img src="${imageUrl}" alt="${data.title}"/>
        </a>
      </div>`;
            this.insertHtml(thumbnail);
            sub.unsubscribe();
        });
    }

    nextNode(node) {
        if (node.hasChildNodes()) {
            return node.firstChild;
        } else {
            while (node && !node.nextSibling) {
                node = node.parentNode;
            }
            if (!node) {
                return null;
            }
            return node.nextSibling;
        }
    }

    getRangeSelectedNodes(range, includePartiallySelectedContainers) {
        let node = range.startContainer;
        const endNode = range.endContainer;
        let rangeNodes = [];

        // Special case for a range that is contained within a single node
        if (node === endNode) {
            rangeNodes = [node];
        } else {
            // Iterate nodes until we hit the end container
            while (node && node !== endNode) {
                rangeNodes.push((node = this.nextNode(node)));
            }

            // Add partially selected nodes at the start of the range
            node = range.startContainer;
            while (node && node !== range.commonAncestorContainer) {
                rangeNodes.unshift(node);
                node = node.parentNode;
            }
        }

        // Add ancestors of the range container, if required
        if (includePartiallySelectedContainers) {
            node = range.commonAncestorContainer;
            while (node) {
                rangeNodes.push(node);
                node = node.parentNode;
            }
        }

        return rangeNodes;
    }

    getSelectedNodes() {
        const nodes = [];
        if (this.doc.getSelection) {
            const sel = this.doc.getSelection();
            for (let i = 0, len = sel.rangeCount; i < len; ++i) {
                nodes.push.apply(nodes, this.getRangeSelectedNodes(sel.getRangeAt(i), true));
            }
        }
        return nodes;
    }

    replaceWithOwnChildren(el) {
        const parent = el.parentNode;
        while (el.hasChildNodes()) {
            parent.insertBefore(el.firstChild, el);
        }
        parent.removeChild(el);
    }

    removeSelectedElements(tagNames) {
        const tagNamesArray = tagNames.toLowerCase().split(',');
        this.getSelectedNodes().forEach(node => {
            if (node.nodeType === 1 && tagNamesArray.indexOf(node.tagName.toLowerCase()) > -1) {
                // Remove the node and replace it with its children
                this.replaceWithOwnChildren(node);
            }
        });
    }
}
